import { KeyboardArrowLeft } from "@mui/icons-material";
import { Button, CircularProgress, Dialog, DialogContent } from "@mui/material";
import { Field, FormikProvider, useFormik } from "formik";
import React from "react";
import * as Yup from "yup";

import { InputField } from "components/InputField";
import { createAutopilotUser } from "services/AutopilotService";

import styles from "../ProfilePage.module.scss";

interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  defaultEmail: string;
  defaultFirstName: string;
  onCreated: (apiKey: string) => void;
}

const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  open,
  onClose,
  defaultEmail,
  defaultFirstName,
  onCreated,
}) => {
  const formik = useFormik({
    initialValues: {
      email: defaultEmail,
      address_line1: "",
      address_line2: "",
      city: "",
      zip_code: "",
      country: "",
      first_name: defaultFirstName,
      last_name: "",
      company_name: "",
      api_key_label: "autopilot api key",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string().email().required("Email is required"),
      address_line1: Yup.string().nullable(),
      address_line2: Yup.string().nullable(),
      city: Yup.string().nullable(),
      zip_code: Yup.string().nullable(),
      country: Yup.string().nullable(),
      first_name: Yup.string().required("First name is required"),
      last_name: Yup.string().nullable(),
      company_name: Yup.string().nullable(),
      api_key_label: Yup.string().nullable(),
    }),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        const address = [
          values.address_line1,
          values.address_line2,
          [values.city, values.zip_code].filter(Boolean).join(" "),
          values.country,
        ]
          .map((line) => line.trim())
          .filter(Boolean)
          .join("\n");
        const res = await createAutopilotUser({
          email: values.email,
          address,
          first_name: values.first_name,
          last_name: values.last_name || undefined,
          company_name: values.company_name || undefined,
          api_key_label: values.api_key_label || undefined,
        });
        setStatus("success");
        onCreated(res.api_key);
      } catch (err) {
        setStatus(
          (err as { error?: string })?.error || "Failed to generate API key.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: styles.apiKeyModal,
          sx: { minWidth: 520 },
        },
      }}
    >
      <DialogContent className={styles.apiKeyModalContent}>
        <div className={styles.apiKeyModalBack} onClick={handleClose}>
          <KeyboardArrowLeft
            fontSize="small"
            className={styles.apiKeyModalBackArrow}
          />
          <span>Back</span>
        </div>
        <h2 className={styles.apiKeyModalTitle}>Generate Your API Key</h2>
        <p className={styles.apiKeyModalSubtitle}>
          You'll only see this key once. Please copy and store it in a secure
          location.
        </p>
        <FormikProvider value={formik}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
          >
            <div className={styles.apiKeyModalFields}>
              {formik.status && formik.status !== "success" && (
                <p className={styles.developerError}>{formik.status}</p>
              )}
              <Field
                name="email"
                component={InputField}
                label="Email"
                inputClassName={styles.profileInputCompact}
                disabled
              />
              <Field
                name="address_line1"
                component={InputField}
                label="Address Line 1 (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="address_line2"
                component={InputField}
                label="Address Line 2 (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="city"
                component={InputField}
                label="City (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="zip_code"
                component={InputField}
                label="Zip Code (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="country"
                component={InputField}
                label="Country (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="first_name"
                component={InputField}
                label="First Name"
                inputClassName={styles.profileInputCompact}
                disabled
              />
              <Field
                name="last_name"
                component={InputField}
                label="Last Name (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="company_name"
                component={InputField}
                label="Company Name (optional)"
                inputClassName={styles.profileInputCompact}
              />
              <Field
                name="api_key_label"
                component={InputField}
                label="API Key Label"
                inputClassName={styles.profileInputCompact}
              />
            </div>
            <Button
              type="submit"
              className={styles.apiKeyModalSubmitBtn}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Generate API Key →"
              )}
            </Button>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
};

export default CreateApiKeyModal;
